/**
 * Protocol constants shared by client and server (PROTOCOL.md sections 1, 5.3, 7 and 8). The server
 * rejects with them; the client never sends past them.
 */

/**
 * Header version byte and the `v` field of text frames: 6 since the PvP flag byte's switching bit (ADR-076's
 * amendment of 2026-09-28), after 5 for Phase 2's snapshot layout (the Level component) and the combat messages
 * (ADR-074 decision 13), 4 for chat's commands (ADR-068's amendment "chat commands, Discord
 * style", item 7), 3 for `welcome`'s rules version (ADR-069 decision 9) and 2 for names in the snapshot (ADR-067
 * decision 5). A page or a gateway of another version is closed with 4000 at `hello`, before its token is read.
 */
export const PROTOCOL_VERSION = 6;

/**
 * WebSocket subprotocol name. It names the framing and stays when the version moves, so a page of another
 * version reaches `hello` and hears 4000 rather than being refused at the upgrade (ADR-067 decision 5).
 */
export const SUBPROTOCOL = "odysseytales.v1";

/**
 * Largest client binary frame in bytes.
 */
export const MAX_CLIENT_BINARY_BYTES = 64;

/**
 * Largest text frame in bytes, enforced at the transport boundary before parsing.
 */
export const MAX_TEXT_FRAME_BYTES = 4096;

/**
 * Most `input` messages accepted per second per connection.
 */
export const MAX_INPUTS_PER_SECOND = 30;

/**
 * Ticks between periodic keyframes for a client, five seconds (ADR-034, P1.5-T6 amendment). A periodic
 * keyframe carries the whole view, so its bytes over the interval are the budget knob: about 17 bytes per
 * entity in view once per interval.
 */
export const KEYFRAME_INTERVAL = 100;

/**
 * Ticks between sends of an entity in interest tier 2, the quarter rate (ADR-034 decision 3): the map server sends
 * it on one tick in this many, and the client's interpolator carries it across the ticks between.
 */
export const QUARTER_RATE_TICKS = 4;

/**
 * The most movement budget a character banks on the map server (ADR-031, anti-cheat audit finding 2): each tick adds
 * one step and each consumed input with a direction spends one, so over any W ticks a character takes at most
 * W + MOVE_BUDGET_CAP - 1 steps however fast its client sends, while a bunch of inputs a jittery link delivered
 * together after quiet ticks still steps in full. Three covers a bunch after a 100 ms gap; an input past the budget is
 * still consumed and acknowledged, so the queue always drains, and the page's reconcile corrects the step it predicted.
 * It is here, beside `QUARTER_RATE_TICKS`, so a rule that bounds what a character covers between two quarter-rate views
 * reads both without the map server (ADR-116 section G, F3).
 */
export const MOVE_BUDGET_CAP = 3;

/**
 * Views of recent ticks the server and the client each keep per client, 1.6 seconds. A map server bases every
 * delta on the newest (ADR-034, P1.5-T6 amendments); a client keeps the older ones because a map server before
 * protocol 1.4.9 based deltas on the tick it acknowledged, older by the round trip.
 */
export const SNAPSHOT_RING = 32;

/**
 * Largest string the text schemas accept for a zone token.
 */
export const MAX_ZONE_TOKEN_CHARS = 512;

/**
 * Most `interact` frames a connection may send per second (PROTOCOL.md section 7), on a window of its own.
 */
export const MAX_INTERACTS_PER_SECOND = 10;

/**
 * Most `act` frames a connection may send per second, on a window of its own (ADR-073 decision 2). An account has one
 * character in the world at a time, so the connection's window is the account's too.
 */
export const MAX_ACTS_PER_SECOND = 10;

/**
 * Most `target` frames a connection may send per second, on a window of its own (ADR-034's amendment of 2026-10-01);
 * three windows over in a row close 4003.
 */
export const MAX_TARGETS_PER_SECOND = 10;

/**
 * Most `pvpFlagToggle` frames a connection may send per second, on a window of its own (ADR-076 decision 1); the
 * connection's window is the account's, as for `act`.
 */
export const MAX_FLAG_TOGGLES_PER_SECOND = 4;

/**
 * Sustained `travelAsk`, `travel` and `travelCancel` frames a connection may send per second, together on one budget of
 * their own (ADR-137 decision 13); the connection's budget is the account's, as for `act`.
 */
export const TRAVEL_REQUESTS_PER_SECOND = 2;

/**
 * The burst that budget admits at once; a frame past it is dropped, and the excess closes 4003.
 */
export const TRAVEL_REQUEST_BURST = 4;

/**
 * Most `respawn` frames a connection may send per second, on a window of its own (ADR-073's amendment "the respawn is
 * the player's"); three windows over in a row close 4003.
 */
export const MAX_RESPAWNS_PER_SECOND = 4;

/**
 * Most item requests a connection may send per second, `useItem`, `bagMove` and `lootTake` together on one window of
 * their own (ADR-091 decision 10); three windows over in a row close 4003.
 */
export const MAX_ITEM_REQUESTS_PER_SECOND = 5;

/**
 * Most `barSet` frames a connection may send per second, on a window of its own (ADR-121 decision 5.2); three windows
 * over in a row close 4003. An honest page holds one set in flight and never reaches it.
 */
export const MAX_BAR_SETS_PER_SECOND = 4;

/**
 * Most `toolBeltSet` frames a connection may send per second, on a window of its own (ADR-142 decision 3b); three windows
 * over in a row close 4003. A player cycling tools with Q sends one set per press while the last is still in flight, so
 * the window is above what a held key repeats at.
 */
export const MAX_TOOL_BELT_SETS_PER_SECOND = 10;

/**
 * The most slots the inventory messages name, a slot from 0 to one less: the bound `character_items.slot` checks
 * (ADR-091 decision 5), so the schema admits any bag the database holds. A `bag` of every slot is 6 to 12 KB, past
 * `MAX_TEXT_FRAME_BYTES`, which caps only what a client sends; nothing caps a server's text frame.
 */
export const MAX_WIRE_BAG_SLOTS = 200;

/**
 * Most client messages a connection may send per second that the catalogue declares and this build does not yet
 * handle, dropped and counted on a window of their own (ADR-074 decision 1); three windows over in a row close 4003.
 */
export const MAX_NOT_READY_PER_SECOND = 10;

/**
 * Longest verb name in an `interact` frame.
 */
export const MAX_VERB_CHARS = 32;

/**
 * Longest `interaction.text`.
 */
export const MAX_INTERACTION_TEXT_CHARS = 1024;

/**
 * Longest `text` a chat request's field table admits, in UTF-16 units (ADR-068 decision 2): the frame's own
 * schema, inside the 4 KB text frame. A longer string is a malformed frame; a text inside it and past the chat
 * text rule's 200 code points is refused, not closed.
 */
export const MAX_CHAT_FRAME_CHARS = 1024;

/**
 * Most chat frames (`say`, `whisper`, `block`, `unblock`) a connection may send per second, on a window of its
 * own (ADR-068 decision 4). Past it a frame is dropped, counted and not answered, and three windows over in a
 * row close with 4003, as for `interact`.
 */
export const MAX_CHAT_FRAMES_PER_SECOND = 10;

/**
 * Most families one account's block list holds (ADR-068 decision 6), and so the most names a `blocks` message
 * carries.
 */
export const MAX_BLOCKED_FAMILIES = 100;

/**
 * Largest error message the server sends.
 */
export const MAX_ERROR_MESSAGE_CHARS = 256;

/**
 * Largest value a `u16` field carries.
 */
export const MAX_U16 = 0xffff;

/**
 * Largest value a `u32` field carries.
 */
export const MAX_U32 = 0xffffffff;

/**
 * The handle that names no entity in a `u16` handle field (ADR-074 decision 2). A map server's entity capacity is at
 * most this, so its handles run from 0 to 65,534 and never reach it.
 */
export const NO_HANDLE = 0xffff;

/**
 * WebSocket close codes (PROTOCOL.md section 8). 4008 closes a connection a GM kicked or banned (ADR-056 and its
 * amendment of 2026-10-04); the `moderation` message sent before it carries the reason. 4011 closes every connection when maintenance
 * starts and refuses a `hello` while it runs (ADR-078 decision 5). 4012 refuses a `hello` for a character of an
 * account whose other character lingers in a fight (ADR-084 decision 14). 4013 refuses a `hello` that would take the login
 * queue past its waiting limit (ADR-127 decisions 5 and 6).
 */
export const CloseCode = {
  VersionMismatch: 4000,
  AuthFailed: 4001,
  Malformed: 4002,
  RateLimited: 4003,
  HandoffFailed: 4004,
  ShuttingDown: 4005,
  ChannelFull: 4006,
  WorldUpdated: 4007,
  Moderated: 4008,
  SessionReplaced: 4009,
  Quiet: 4010,
  Maintenance: 4011,
  CharacterLingering: 4012,
  QueueFull: 4013,
} as const;
export type CloseCode = (typeof CloseCode)[keyof typeof CloseCode];
